/**
 * MotivationalQuestionView — Single Focused Question Card
 *
 * Renders one question at a time with:
 *  - Collapsible "Why we ask this" hint section
 *  - Textarea that fills ALL remaining space in the dialog body
 *  - Word + character count inline below textarea
 *  - Inline validation feedback on blur
 *
 * Layout strategy (state-of-the-art for fixed-height dialogs):
 *  - Question header + constraints: shrink-0 (compact, top)
 *  - Textarea: fills remaining space via relative/absolute positioning
 *  - Metrics + validation: shrink-0 (compact, bottom)
 *  - Textarea scrolls internally — no auto-grow layout shifts
 *
 * @module copilot/widgets/motivational-form
 */

import { cn } from '@tonnie/foundation';
import { useTranslation, useLocale } from '@tonnie/locale';
import {
  Badge,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Textarea,
} from '@tonnie/ui';
import { AlertCircle, CheckCircle2, ChevronDown, Info, XCircle } from '@tonnie/assets';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import type { IQuestion, IValidationResult } from './motivational-form.types';
import {
  countWords,
  MOTIVATIONAL_MIN_WORDS,
  validateAnswer,
  validationMessage,
} from './motivational-form.utils';

interface MotivationalQuestionViewProps {
  question: IQuestion;
  /** 0-based index of the question */
  index: number;
  /** Total number of questions */
  total: number;
  /** Current answer value */
  answer: string;
  /** Called on every keystroke */
  onAnswerChange: (value: string) => void;
  /** External validation result (set by parent on navigation) */
  validationResult?: IValidationResult;
  /** Called when field validation completes (on blur) */
  onValidate?: (result: IValidationResult | undefined) => void;
}

export function MotivationalQuestionView({
  question,
  index,
  total,
  answer,
  onAnswerChange,
  validationResult,
  onValidate,
}: MotivationalQuestionViewProps) {
  const { formatNumber } = useLocale();
  const { t } = useTranslation();
  const questionLabelId = useId();
  const [hintOpen, setHintOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const charCount = answer.length;
  const wordCount = countWords(answer);
  const maxLength = question.maxLength;

  // Auto-focus textarea when question changes
  useEffect(() => {
    const timer = setTimeout(() => textareaRef.current?.focus(), 200);
    return () => clearTimeout(timer);
  }, [question.questionId]);

  // Validate on blur
  const handleBlur = useCallback(() => {
    const result = validateAnswer(answer, {
      required: question.required,
      maxChars: question.maxLength,
    });
    onValidate?.(result);
  }, [answer, question, onValidate]);

  // Character count colour — flags over-max (destructive) and near-max (amber)
  // so the ONE blocking rule left is visible live, before the user submits and
  // hits it. It no longer flags "below minimum": the advisory quality rule is
  // about words, so colouring the character counter for it pointed at the wrong
  // number. The word counter carries that signal instead.
  const charRatio = maxLength ? charCount / maxLength : 0;
  const charColor =
    charRatio > 1
      ? 'text-destructive-accessible font-semibold'
      : charRatio > 0.9
        ? 'text-tone-amber font-medium'
        : 'text-muted-foreground';

  // Advisory only — amber, never red, and it gates nothing.
  const thin = wordCount > 0 && wordCount < MOTIVATIONAL_MIN_WORDS;

  // Validation border
  const borderColor =
    validationResult?.status === 'ERROR'
      ? 'border-destructive focus-visible:ring-destructive'
      : validationResult?.status === 'WARNING'
        ? 'border-tone-amber-solid focus-visible:ring-tone-amber-solid'
        : validationResult?.status === 'OK'
          ? 'border-success focus-visible:ring-success'
          : 'border-border';

  const hasHint = question.concept || question.context;

  return (
    <motion.div
      key={question.questionId}
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col h-full min-h-0"
      data-testid={`widget-motivational_form-question-${index}`}
    >
      {/* ── Top: Question + metadata (compact, fixed height) ── */}
      <div className="shrink-0 mb-3">
        {/* Question number + required badge */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-primary-accessible">{index + 1}</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {t('copilot.questionNofM', {
              n: index + 1,
              m: total,
            })}
          </span>
          <Badge
            variant="outline"
            className={cn(
              'text-xs font-semibold px-2 py-0.5 rounded-full ml-auto',
              question.required
                ? 'bg-primary/10 text-primary-accessible border-primary/30'
                : 'bg-muted text-muted-foreground border-border'
            )}
          >
            {question.required ? t('copilot.required') : t('copilot.optional')}
          </Badge>
        </div>

        {/* Question text */}
        <h3
          id={questionLabelId}
          className="text-base font-semibold text-foreground leading-snug mb-2"
        >
          {question.label}
        </h3>

        {/* Constraints row: suggested length • Max chars • Why we ask
            "Please answer in English" is gone with the rule it announced. It
            told the citizen to do something the platform does not require and
            actively does not want here: the backend stores `sourceLocale` and
            translates for scoring, and forcing a second language into the
            MOTIVATION free-text degrades the very signal being measured. An
            instruction the product no longer enforces is worse than no
            instruction — people obey it. */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">
            {t('copilot.suggestedWords', { min: MOTIVATIONAL_MIN_WORDS })}
          </span>
          {maxLength && (
            <>
              <span className="text-xs text-muted-foreground">&bull;</span>
              <span className="text-xs text-muted-foreground">
                {t('copilot.maxChars', {
                  max: formatNumber(maxLength),
                })}
              </span>
            </>
          )}
          {hasHint && (
            <>
              <span className="text-xs text-muted-foreground">&bull;</span>
              <Collapsible open={hintOpen} onOpenChange={setHintOpen}>
                <CollapsibleTrigger className="inline-flex items-center gap-1 text-xs font-medium text-primary-accessible hover:text-primary-accessible/80 transition-colors cursor-pointer">
                  <Info className="w-3 h-3" />
                  <span>{t('copilot.whyWeAsk')}</span>
                  <ChevronDown
                    className={cn(
                      'w-3 h-3 transition-transform duration-200',
                      hintOpen && 'rotate-180'
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <Card className="border border-primary/20 bg-primary/5">
                    <CardContent className="p-3">
                      {question.concept && (
                        <p className="text-xs leading-relaxed text-foreground/80 mb-1">
                          {question.concept}
                        </p>
                      )}
                      {question.context && (
                        <p className="text-xs leading-relaxed text-muted-foreground">
                          {question.context}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </div>
      </div>

      {/* ── Middle: Textarea (fills ALL remaining space) ── */}
      <div className="flex-1 min-h-[120px] relative">
        <Textarea
          ref={textareaRef}
          aria-labelledby={questionLabelId}
          value={answer}
          onChange={(e) => onAnswerChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={t('copilot.typeAnswer')}
          maxLength={maxLength}
          data-testid={`widget-motivational_form-input-${index}`}
          /**
           * The validation outcome, as data rather than as styling.
           *
           * `borderColor` below is the only other externally visible signal, and
           * it cannot be asserted on: the Textarea primitive's base class carries
           * `aria-invalid:border-destructive` UNCONDITIONALLY, so a substring
           * match for `border-destructive` is true on every render — it cannot
           * tell "this answer was rejected" from "this component declares a
           * conditional style". An E2E gate written against that class passed on
           * a valid Portuguese answer and reported the deleted stoplist as
           * reintroduced.
           *
           * Same reasoning as `data-camera-error` on the pitch-recorder banner.
           */
          data-validation-status={validationResult?.status ?? 'none'}
          className={cn(
            'absolute inset-0 w-full h-full p-4 text-sm resize-none rounded-lg transition-colors',
            borderColor,
            'focus-visible:ring-2 focus-visible:ring-offset-2'
          )}
        />
      </div>

      {/* ── Bottom: Metrics + validation (compact, fixed height) ── */}
      <div className="shrink-0">
        {/* Word count + char count */}
        <div className="flex justify-between items-center mt-1.5 px-1">
          <span className={cn('text-xs', thin ? 'text-tone-amber' : 'text-muted-foreground')}>
            {t('copilot.words', { count: wordCount })}
          </span>
          {maxLength && (
            <span className={cn('text-xs', charColor)}>
              {formatNumber(charCount)} / {formatNumber(maxLength)}
            </span>
          )}
        </div>

        {/* Validation feedback */}
        {validationResult && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              'mt-1.5 p-2.5 rounded-lg border flex gap-2',
              validationResult.status === 'OK' && 'bg-success/10 border-success/30',
              validationResult.status === 'WARNING' &&
                'bg-tone-amber-subtle border-tone-amber-line',
              validationResult.status === 'ERROR' && 'bg-destructive/10 border-destructive/30'
            )}
          >
            <div className="shrink-0 mt-0.5">
              {validationResult.status === 'OK' ? (
                <CheckCircle2 className="w-4 h-4 text-success-accessible" />
              ) : validationResult.status === 'WARNING' ? (
                <AlertCircle className="w-4 h-4 text-tone-amber" />
              ) : (
                <XCircle className="w-4 h-4 text-destructive-accessible" />
              )}
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground flex-1">
              {validationMessage(validationResult, t, formatNumber)}
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
